
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase/client';
import { parseISO, format, isAfter, isBefore, isEqual } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';

export interface VideoEvent {
  id: string;
  title: string;
  date: Date;
  time: string;
  type: string;
  profileId: string;
  videoId?: string;
  status?: string;
  contentType?: string;
  hasNotes?: boolean;
}

export interface VideoFilterOptions {
  title?: string;
  startDate?: Date;
  endDate?: Date;
  statuses?: string[];
  contentTypes?: string[];
  showNotes?: boolean;
}

export function useVideoEvents(selectedProfiles: string[], filterOptions?: VideoFilterOptions) {
  const [videoEvents, setVideoEvents] = useState<VideoEvent[]>([]);
  const [allVideoEvents, setAllVideoEvents] = useState<VideoEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch videos from Supabase
  useEffect(() => {
    const fetchVideos = async () => {
      if (!selectedProfiles || selectedProfiles.length === 0) {
        setAllVideoEvents([]);
        setVideoEvents([]);
        return;
      }

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('videos')
          .select('*')
          .in('profile_id', selectedProfiles);

        if (error) {
          throw error;
        }

        console.log("Fetched videos from Supabase:", data);

        // If no data, set empty arrays
        if (!data || data.length === 0) {
          setAllVideoEvents([]);
          setVideoEvents([]);
          setIsLoading(false);
          return;
        }

        // Convert the database results to our VideoEvent format
        const formattedEvents: VideoEvent[] = data.map(video => ({
          id: video.id || `temp-${Math.random().toString(36).substring(2, 9)}`,
          title: video.title || 'Vidéo sans titre',
          date: video.published_at ? parseISO(video.published_at) : new Date(),
          time: video.published_at ? format(parseISO(video.published_at), 'HH:mm') : '00:00',
          type: 'youtube',
          profileId: video.profile_id,
          videoId: video.youtube_id || video.video_id,
          status: video.status || 'published', // Default status if not provided
          contentType: video.content_type || 'video', // Default content type for YouTube videos
          hasNotes: Boolean(video.notes) // Check if the video has any notes
        }));

        console.log("Formatted video events:", formattedEvents);
        setAllVideoEvents(formattedEvents);
      } catch (error) {
        console.error('Error fetching videos:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les vidéos',
          variant: 'destructive',
        });
        // Set empty arrays on error to prevent undefined issues
        setAllVideoEvents([]);
        setVideoEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideos();
  }, [selectedProfiles, toast]);

  // Apply filters to videos
  useMemo(() => {
    if (!allVideoEvents || allVideoEvents.length === 0) {
      setVideoEvents([]);
      return;
    }

    if (!filterOptions) {
      setVideoEvents(allVideoEvents);
      console.log("No filters applied, showing all videos:", allVideoEvents.length);
      return;
    }

    console.log("Applying filters:", filterOptions);
    console.log("All video events before filtering:", allVideoEvents);

    let filteredEvents = [...allVideoEvents];

    // Filter by title
    if (filterOptions.title && filterOptions.title.trim() !== '') {
      const searchTerm = filterOptions.title.toLowerCase().trim();
      filteredEvents = filteredEvents.filter(event => 
        event.title.toLowerCase().includes(searchTerm)
      );
      console.log("After title filter:", filteredEvents.length);
    }

    // Filter by date range
    if (filterOptions.startDate) {
      filteredEvents = filteredEvents.filter(event => {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        
        const startDate = new Date(filterOptions.startDate!);
        startDate.setHours(0, 0, 0, 0);
        
        if (filterOptions.endDate) {
          const endDate = new Date(filterOptions.endDate);
          endDate.setHours(23, 59, 59, 999);
          
          return (
            (isAfter(eventDate, startDate) || isEqual(eventDate, startDate)) && 
            (isBefore(eventDate, endDate) || isEqual(eventDate, endDate))
          );
        }
        
        return isEqual(eventDate, startDate) || isAfter(eventDate, startDate);
      });
      console.log("After date range filter:", filteredEvents.length);
    }

    // Filter by publication status
    if (filterOptions.statuses && filterOptions.statuses.length > 0) {
      filteredEvents = filteredEvents.filter(event => 
        event.status && filterOptions.statuses?.includes(event.status)
      );
      console.log("After status filter:", filteredEvents.length);
    }

    // Filter by content type
    if (filterOptions.contentTypes && filterOptions.contentTypes.length > 0) {
      filteredEvents = filteredEvents.filter(event => 
        event.contentType && filterOptions.contentTypes?.includes(event.contentType)
      );
      console.log("After content type filter:", filteredEvents.length);
    }

    // Filter by notes
    if (filterOptions.showNotes) {
      filteredEvents = filteredEvents.filter(event => event.hasNotes);
      console.log("After notes filter:", filteredEvents.length);
    }

    console.log("Final filtered events:", filteredEvents);
    setVideoEvents(filteredEvents);
  }, [allVideoEvents, filterOptions]);

  return { videoEvents, isLoading };
}
